"use client";

import Image from "next/image";

import { CalendarBlank, ChartBar, QrCode, Users, Wallet } from "@phosphor-icons/react";
import { motion, type Variants } from "motion/react";

const easeCubic = [0.32, 0.72, 0, 1] as const;

const fadeUpVariant: Variants = {
  hidden: { opacity: 0, y: 80, filter: "blur(16px)" },
  show: {
    opacity: 1,
    y: 0,
    filter: "blur(0px)",
    transition: {
      duration: 1.4,
      ease: easeCubic,
    },
  },
};

export function FeaturesSection() {
  return (
    <section id="features" className="px-4 py-32 md:py-48 max-w-[1400px] mx-auto w-full">
      <motion.div
        initial="hidden"
        whileInView="show"
        viewport={{ once: true, margin: "-100px" }}
        variants={fadeUpVariant}
        className="mb-16 md:mb-24 flex flex-col items-center text-center"
      >
        <span className="rounded-full bg-black/5 border border-black/5 px-4 py-1.5 text-[10px] uppercase tracking-[0.2em] font-semibold text-black/60 mb-6">
          System Capabilities
        </span>
        <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight mb-4 text-[#0A0A0A]">
          Absolute Control.
          <br />
          <span className="text-black/30">Zero Friction.</span>
        </h2>
      </motion.div>

      <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
        {/* Bento Item 1: Academy Overview (Col span 8) */}
        <motion.div
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, margin: "-50px" }}
          variants={fadeUpVariant}
          className="md:col-span-8 group"
        >
          {/* Double-Bezel Outer Shell */}
          <div className="h-full rounded-[2.5rem] p-2 bg-black/5 ring-1 ring-black/5 transition-all duration-700 ease-[cubic-bezier(0.32,0.72,0,1)] group-hover:bg-black/10 group-hover:ring-black/10">
            {/* Double-Bezel Inner Core */}
            <div className="h-full min-h-[450px] rounded-[calc(2.5rem-0.5rem)] bg-white p-8 md:p-10 shadow-[inset_0_1px_1px_rgba(255,255,255,1),0_20px_40px_-15px_rgba(0,0,0,0.05)] relative overflow-hidden flex flex-col transition-all duration-700 ease-[cubic-bezier(0.32,0.72,0,1)] group-hover:shadow-[inset_0_1px_1px_rgba(255,255,255,1),0_30px_60px_-20px_rgba(0,0,0,0.1)] group-hover:-translate-y-1">
              <div className="relative z-10 max-w-xl mb-12">
                <div className="w-12 h-12 rounded-full bg-black/5 flex items-center justify-center mb-6">
                  <ChartBar weight="light" size={24} className="text-[#0A0A0A]" />
                </div>
                <h3 className="text-2xl md:text-3xl font-bold mb-3 text-[#0A0A0A]">Operational Command</h3>
                <p className="text-black/80 font-medium leading-relaxed text-lg">
                  Real-time analytics across your entire institution. Monitor active groups, teacher payroll, and
                  instantly identify outstanding debts or expiring subscriptions.
                </p>
              </div>
              <div className="relative mt-auto self-end w-[80%] md:w-[65%] -mr-[5%] -mb-[5%] translate-y-8 transition-transform duration-700 ease-[cubic-bezier(0.32,0.72,0,1)] group-hover:translate-y-2 group-hover:scale-[1.02]">
                <div className="relative w-full aspect-[16/10] rounded-tl-2xl overflow-hidden shadow-2xl border-t border-l border-black/10">
                  <Image
                    src="/features/overview.webp"
                    alt="Academy Overview Dashboard"
                    fill
                    className="object-cover object-left-top"
                    sizes="(max-width: 768px) 100vw, 50vw"
                  />
                </div>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Bento Item 2: QR Identity (Col span 4) */}
        <motion.div
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, margin: "-50px" }}
          variants={fadeUpVariant}
          className="md:col-span-4 group"
        >
          <div className="h-full rounded-[2.5rem] p-2 bg-black/5 ring-1 ring-black/5 transition-all duration-700 ease-[cubic-bezier(0.32,0.72,0,1)] group-hover:bg-black/10 group-hover:ring-black/10">
            <div className="h-full min-h-[450px] rounded-[calc(2.5rem-0.5rem)] bg-white p-8 md:p-10 shadow-[inset_0_1px_1px_rgba(255,255,255,1),0_20px_40px_-15px_rgba(0,0,0,0.05)] relative overflow-hidden flex flex-col transition-all duration-700 ease-[cubic-bezier(0.32,0.72,0,1)] group-hover:shadow-[inset_0_1px_1px_rgba(255,255,255,1),0_30px_60px_-20px_rgba(0,0,0,0.1)] group-hover:-translate-y-1">
              <div className="relative z-10">
                <div className="w-12 h-12 rounded-full bg-black/5 flex items-center justify-center mb-6 text-[#0A0A0A]">
                  <QrCode weight="light" size={24} />
                </div>
                <h3 className="text-2xl md:text-3xl font-bold mb-3 text-[#0A0A0A]">QR Workflows</h3>
                <p className="text-black/80 font-medium leading-relaxed text-lg">
                  Generate digital identities. Scan students into scheduled classes instantly and track staff attendance
                  with precision.
                </p>
              </div>
              <div className="relative mt-auto self-end w-[85%] -mr-[10%] -mb-[15%] translate-y-4 transition-transform duration-700 ease-[cubic-bezier(0.32,0.72,0,1)] group-hover:translate-y-0 group-hover:rotate-[-2deg]">
                <div className="relative w-full aspect-[4/5] rounded-xl overflow-hidden shadow-2xl border border-black/10">
                  <Image
                    src="/features/print-recipient-student.webp"
                    alt="Student QR Card"
                    fill
                    className="object-cover object-top"
                    sizes="(max-width: 768px) 100vw, 33vw"
                  />
                </div>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Bento Item 3: Financial Mastery (Col span 4) */}
        <motion.div
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, margin: "-50px" }}
          variants={fadeUpVariant}
          className="md:col-span-4 group"
        >
          <div className="h-full rounded-[2.5rem] p-2 bg-black/5 ring-1 ring-black/5 transition-all duration-700 ease-[cubic-bezier(0.32,0.72,0,1)] group-hover:bg-black/10 group-hover:ring-black/10">
            <div className="h-full min-h-[450px] rounded-[calc(2.5rem-0.5rem)] bg-white p-8 md:p-10 shadow-[inset_0_1px_1px_rgba(255,255,255,1),0_20px_40px_-15px_rgba(0,0,0,0.05)] relative overflow-hidden flex flex-col transition-all duration-700 ease-[cubic-bezier(0.32,0.72,0,1)] group-hover:-translate-y-1">
              <div className="relative z-10 mb-8">
                <div className="w-12 h-12 rounded-full bg-black/5 flex items-center justify-center mb-6">
                  <Wallet weight="light" size={24} className="text-[#0A0A0A]" />
                </div>
                <h3 className="text-2xl font-bold mb-3 text-[#0A0A0A]">Financial Mastery</h3>
                <p className="text-black/80 font-medium leading-relaxed text-lg">
                  Track every Dinar. Automate fee collection pipelines, manage session renewals, and track outstanding
                  debts with zero leakage.
                </p>
              </div>
              <div className="relative mt-auto self-start w-[110%] ml-[10%] -mb-[5%] translate-y-6 transition-transform duration-700 ease-[cubic-bezier(0.32,0.72,0,1)] group-hover:translate-y-2">
                <div className="relative w-full aspect-[16/10] rounded-t-xl overflow-hidden shadow-2xl border border-black/10">
                  <Image
                    src="/features/student-payments.webp"
                    alt="Student Payments"
                    fill
                    className="object-cover object-left-top"
                    sizes="(max-width: 768px) 100vw, 33vw"
                  />
                </div>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Bento Item 4: Student & Staff (Col span 8) */}
        <motion.div
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, margin: "-50px" }}
          variants={fadeUpVariant}
          className="md:col-span-8 group"
        >
          <div className="h-full rounded-[2.5rem] p-2 bg-black/5 ring-1 ring-black/5 transition-all duration-700 ease-[cubic-bezier(0.32,0.72,0,1)] group-hover:bg-black/10 group-hover:ring-black/10">
            <div className="h-full min-h-[450px] rounded-[calc(2.5rem-0.5rem)] bg-white p-8 md:p-10 shadow-[inset_0_1px_1px_rgba(255,255,255,1),0_20px_40px_-15px_rgba(0,0,0,0.05)] relative overflow-hidden flex flex-col md:flex-row transition-all duration-700 ease-[cubic-bezier(0.32,0.72,0,1)] group-hover:-translate-y-1">
              <div className="relative z-10 w-full md:w-1/2 mb-12 md:mb-0 md:pr-8">
                <div className="w-12 h-12 rounded-full bg-black/5 flex items-center justify-center mb-6">
                  <Users weight="light" size={24} className="text-[#0A0A0A]" />
                </div>
                <h3 className="text-2xl md:text-3xl font-bold mb-3 text-[#0A0A0A]">People Infrastructure</h3>
                <p className="text-black/80 font-medium leading-relaxed text-lg">
                  Transform chaotic registration into a silent, automated flow. Manage thousands of student profiles,
                  enrollments, and teacher schedules with surgical precision.
                </p>
              </div>
              <div className="relative mt-auto md:mt-0 self-end md:self-auto w-[80%] md:w-[60%] -mr-[5%] md:mr-0 -mb-[5%] md:-mb-[20%] translate-y-8 transition-transform duration-700 ease-[cubic-bezier(0.32,0.72,0,1)] group-hover:translate-y-4">
                <div className="relative w-full aspect-[4/3] rounded-tl-2xl overflow-hidden shadow-2xl border-t border-l border-black/10 z-20">
                  <Image
                    src="/features/student-table.webp"
                    alt="Student Directory"
                    fill
                    className="object-cover object-left-top"
                    sizes="(max-width: 768px) 100vw, 50vw"
                  />
                </div>
                {/* Decorative Z-Axis stacked image */}
                <div className="absolute top-[20%] left-[-20%] w-full aspect-[4/3] rounded-tl-2xl overflow-hidden shadow-xl border border-black/10 z-10 opacity-60">
                  <Image
                    src="/features/teachers-table.webp"
                    alt="Teacher Directory"
                    fill
                    className="object-cover object-left-top"
                    sizes="(max-width: 768px) 100vw, 50vw"
                  />
                </div>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Bento Item 5: Scheduling (Col span 12 - Editorial Split) */}
        <motion.div
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, margin: "-50px" }}
          variants={fadeUpVariant}
          className="md:col-span-12 group mt-6"
        >
          <div className="rounded-[2.5rem] p-2 bg-black/5 ring-1 ring-black/5 transition-all duration-700 ease-[cubic-bezier(0.32,0.72,0,1)] group-hover:bg-black/10 group-hover:ring-black/10">
            <div className="rounded-[calc(2.5rem-0.5rem)] bg-[#FDFBF7] p-8 md:p-16 shadow-[inset_0_1px_1px_rgba(255,255,255,1),0_20px_40px_-15px_rgba(0,0,0,0.05)] relative overflow-hidden flex flex-col md:flex-row items-center justify-between transition-all duration-700 ease-[cubic-bezier(0.32,0.72,0,1)] group-hover:-translate-y-1 border border-[#EAEAEF]">
              <div className="relative z-10 w-full md:w-[45%] mb-12 md:mb-0 md:pr-12">
                <div className="w-12 h-12 rounded-full bg-black/5 flex items-center justify-center mb-6">
                  <CalendarBlank weight="light" size={24} className="text-[#0A0A0A]" />
                </div>
                <h3 className="text-3xl md:text-4xl font-bold mb-4 text-[#0A0A0A]">Architectural Oversight</h3>
                <p className="text-black/80 font-medium leading-relaxed text-lg">
                  Complete visibility into staff scheduling, resource allocation, and daily operations from a single
                  pane of glass. Conflict-free classroom calendars.
                </p>
              </div>
              <div className="w-full md:w-[60%] translate-x-4 md:translate-x-12 translate-y-4 md:translate-y-8 transition-transform duration-700 ease-[cubic-bezier(0.32,0.72,0,1)] group-hover:translate-x-0 group-hover:translate-y-4">
                <div className="relative w-full aspect-[16/10] rounded-tl-3xl overflow-hidden shadow-2xl border-t border-l border-black/10">
                  <Image
                    src="/features/classroom-schedule.webp"
                    alt="Classroom Scheduling Calendar"
                    fill
                    className="object-cover object-left-top"
                    sizes="(max-width: 768px) 100vw, 100vw"
                  />
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
